import { ReactNode, useState } from "react";
import { useQuery } from "react-query";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Mic,
  RotateCcw,
  Search,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { FaSpotify } from "react-icons/fa6";
import { Conversion, ModelInfo } from "@jammable/clientapi";
import { getSubscriptionStatus } from "@jammable/ui-payments-feature/src/utils";
import { customModelUrl, modelListingUrl } from "@jammable/ui-core";
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaTrigger,
} from "@jammable/ui-components/Dialog";
import { Upgrade } from "@jammable/ui-payments-feature";
import UpgradeModal from "../../../../../../../apps/web/components/modals/UpgradeModal";
import { useModal } from "../../../../../../../apps/web/context/ModalContext";
import {
  TranscriptionSegment,
  VoiceImage,
  joinArtistNames,
  reuseVoicesHref,
} from "../../utils";

export interface BlockProps {
  model: Conversion["model"];
  user: Conversion["user"];
  conversion: Conversion;
  signedInUser?: {
    subscription: string;
    customModels: number;
    credits: number;
    subscriptions: any;
    quota: any;
  };
  actionHandler?: (
    actionKey:
      | "createVideo"
      | "rateConversion"
      | "createVideo"
      | "redirectToSettings"
      | "manageSubscription",
  ) => void;
  isUK?: boolean;
}

const voicesOf = (conversion: Conversion): Partial<ModelInfo>[] =>
  conversion?.models && conversion.models.length > 0
    ? conversion.models
    : conversion?.model
      ? [conversion.model as Partial<ModelInfo>]
      : [];

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <div className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.06em] tw-text-jam-muted">
    {children}
  </div>
);

const CardHeading = ({ children }: { children: ReactNode }) => (
  <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-inherit">
    {children}
  </h2>
);

const RowHeading = ({ children }: { children: ReactNode }) => (
  <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-tracking-[-0.03em] tw-text-jam-ink md:tw-text-[26px]">
    {children}
  </h2>
);

// "How did it come out?" thumbs. Posts the same 1-5 rating the old emoji
// row did (thumbs up = 5, thumbs down = 1), then says thanks.
export const RateConversion = ({ model, conversion }: BlockProps) => {
  const [hasRated, setHasRated] = useState<boolean>(false);

  const handleRating = async ({ value }: { value: number }) => {
    const headers: RequestInit["headers"] = {
      "Content-Type": "application/json",
    };

    fetch("/api/other/rateConversion", {
      method: "POST",
      headers,
      body: JSON.stringify({
        rating: value,
        modelId: model?.urlParam,
        conversionId: conversion?._id,
      }),
    }).catch(() => {});
    setHasRated(true);
  };

  if (conversion.status !== "success") return null;

  if (hasRated) {
    return (
      <span className="tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
        Thanks for telling us
      </span>
    );
  }

  const thumb =
    "tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-text-jam-ink tw-transition-colors hover:tw-text-jam-purple";

  return (
    <span className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
      How did it come out?
      <button
        aria-label="Came out well"
        className={thumb}
        onClick={() => handleRating({ value: 5 })}
      >
        <ThumbsUp size={16} strokeWidth={2.4} />
      </button>
      <button
        aria-label="Came out badly"
        className={thumb}
        onClick={() => handleRating({ value: 1 })}
      >
        <ThumbsDown size={16} strokeWidth={2.4} />
      </button>
    </span>
  );
};

// Lyrics from the cover's transcription. `activeLine` follows the player;
// we show the line before it and the next two.
export const Lyrics = ({
  transcription,
  activeLine,
}: {
  transcription: TranscriptionSegment[] | null;
  activeLine: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!transcription || transcription.length === 0) return null;

  const lines = transcription
    .map((seg, i) => ({ i, text: (seg.text || "").trim() }))
    .filter((l) => l.text);
  if (lines.length === 0) return null;

  let windowed = lines;
  if (!expanded) {
    const found = lines.findIndex((l) => l.i >= activeLine);
    const pos = found < 0 ? lines.length - 1 : found;
    const from = Math.max(0, Math.min(pos - 1, lines.length - 4));
    windowed = lines.slice(from, from + 4);
  }

  return (
    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2.5 tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-4 tw-text-jam-ink md:tw-rounded-[30px] md:tw-p-5">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <CardHeading>Lyrics</CardHeading>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-bold tw-text-jam-muted hover:tw-text-jam-purple"
        >
          {expanded ? "Follow the song" : "Show all"}
        </button>
      </div>
      <div
        className={`tw-flex tw-flex-col tw-text-[15px] tw-font-bold tw-leading-[1.7] tw-text-[#B3A3C8] ${expanded ? "tw-max-h-80 tw-overflow-y-auto" : ""}`}
      >
        {windowed.map((l) => (
          <span
            key={l.i}
            className={
              l.i === activeLine
                ? "tw-text-lg tw-font-extrabold tw-text-jam-ink"
                : ""
            }
          >
            {l.text}
          </span>
        ))}
      </div>
    </div>
  );
};

// The way into /studio (apps/web/pages/studio/[shortid].tsx): a finished
// cover becomes a video there, with its lyrics, the voice's photo and a
// trim, made in the browser. Editing is open to everyone; exporting needs a
// plan (the studio opens the paywall). Covers that already got a Marble Run
// or template video before they were made skip it.
export const CreateVideo = ({ conversion }: BlockProps) => {
  const hasPreSelectedTemplate =
    conversion.videoSettings?.template ||
    conversion.videoSettings?.externalTemplate;
  if (hasPreSelectedTemplate || conversion.status !== "success") return null;
  const voice = (conversion as any).models?.[0];
  return (
    <Link
      href={`/studio/${conversion.shortid}`}
      className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-4 tw-rounded-[26px] tw-bg-jam-ink tw-p-4 tw-text-white tw-no-underline md:tw-rounded-[30px] md:tw-p-5"
    >
      {/* A tiny likeness of the video: the voice, a lyric. */}
      <div className="tw-relative tw-flex tw-h-[150px] tw-w-[84px] tw-shrink-0 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-overflow-hidden tw-rounded-[18px] tw-bg-gradient-to-b tw-from-jam-deep tw-to-jam-purple md:tw-h-[170px] md:tw-w-[96px]">
        <span className="tw-relative tw-h-12 tw-w-12 tw-overflow-hidden tw-rounded-full tw-ring-[3px] tw-ring-jam-lime">
          {voice?.image?.url && (
            <Image
              fill
              sizes="48px"
              src={voice.image.url}
              alt=""
              className="tw-object-cover"
              quality={50}
            />
          )}
        </span>
        <span className="tw-rounded-full tw-bg-white tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-black tw-text-jam-ink">
          la la la
        </span>
      </div>
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
        <CardHeading>Turn it into a video</CardHeading>
        <div className="tw-text-[13px] tw-font-semibold tw-text-jam-soft">
          Lyrics on screen, the voice&apos;s photo, the best 30 seconds. Ready
          for TikTok and Reels.
        </div>
        <span className="tw-rounded-full tw-bg-jam-lime tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
          Make a video
        </span>
      </div>
    </Link>
  );
};

// "Same song, another voice": trending voices (minus the ones already on
// this cover) plus a way into the full library. Replaces the old "Retry with
// another voice" block, which only linked to the library.
export const AnotherVoice = ({ conversion }: BlockProps) => {
  const { data } = useQuery<{ models?: any[] }>(
    ["cover-page-trending-voices"],
    async () => {
      const res = await fetch("/api/home/getTrendingVoices?limit=12");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    { staleTime: 5 * 60 * 1000, enabled: conversion.status === "success" },
  );

  if (conversion.status !== "success") return null;

  const usedIds = new Set(voicesOf(conversion).map((m) => m._id));
  const suggestions = (data?.models || [])
    .filter((m) => !usedIds.has(m._id))
    .slice(0, 5);

  const tile =
    "tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-gap-2 tw-rounded-[26px] tw-bg-jam-lav tw-px-2 tw-py-3.5 tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-text-jam-purple";

  return (
    <section className="tw-flex tw-flex-col tw-gap-3.5">
      <RowHeading>Same song, another voice</RowHeading>
      <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-6 sm:tw-gap-3">
        {suggestions.map((m) => (
          <Link key={m._id} href={`/${m.urlParam}`} className={tile}>
            <div className="tw-relative tw-h-[60px] tw-w-[60px] tw-overflow-hidden tw-rounded-full tw-bg-white">
              <VoiceImage
                model={m}
                fill
                sizes="60px"
                className="tw-object-cover"
              />
            </div>
            <span className="tw-line-clamp-1 tw-w-full tw-text-center tw-text-[13px] tw-font-extrabold">
              {m.artistname}
            </span>
            <span className="tw-text-[11px] tw-font-extrabold tw-text-jam-purple">
              Use voice
            </span>
          </Link>
        ))}
        <Link href={modelListingUrl()} className={tile}>
          <div className="tw-flex tw-h-[60px] tw-w-[60px] tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-jam-purple">
            <Search size={22} />
          </div>
          <span className="tw-text-[13px] tw-font-extrabold">Search</span>
          <span className="tw-text-[11px] tw-font-extrabold tw-text-jam-purple">
            All voices
          </span>
        </Link>
      </div>
    </section>
  );
};

const KeepGoingRow = ({
  icon,
  iconBg,
  title,
  sub,
}: {
  icon: ReactNode;
  iconBg: string;
  title: string;
  sub: string;
}) => (
  <>
    <span
      className={`tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[14px] tw-text-jam-ink ${iconBg}`}
    >
      {icon}
    </span>
    <div className="tw-min-w-0 tw-flex-grow tw-text-left">
      <div className="tw-text-sm tw-font-extrabold tw-text-jam-ink">
        {title}
      </div>
      <div className="tw-line-clamp-1 tw-text-xs tw-font-semibold tw-text-jam-muted">
        {sub}
      </div>
    </div>
    <ArrowRight size={16} className="tw-shrink-0 tw-text-jam-muted" />
  </>
);

const keepGoingItem =
  "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[20px] tw-border-0 tw-bg-white tw-p-3 tw-font-[inherit] tw-no-underline tw-transition-colors hover:tw-bg-jam-line/50";

// Owner-only "Keep going" card. Folds in what used to be separate blocks:
// reuse voices, browse voices, train a voice, and the plan upsells (Basic ->
// Creator via the Upgrade sheet; no plan -> the upgrade modal).
export const KeepGoing = ({
  conversion,
  signedInUser,
  actionHandler,
}: BlockProps) => {
  const { openModal } = useModal();
  const voices = voicesOf(conversion);
  const plan = signedInUser
    ? getSubscriptionStatus(signedInUser?.subscriptions)
    : null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[30px] tw-bg-jam-lav tw-p-5">
      <Eyebrow>Keep going</Eyebrow>
      {voices.length > 0 && (
        <Link href={reuseVoicesHref(voices)} className={keepGoingItem}>
          <KeepGoingRow
            icon={<RotateCcw size={18} />}
            iconBg="tw-bg-jam-lime"
            title={
              voices.length === 1
                ? "New song, same voice"
                : "New song, same voices"
            }
            sub={joinArtistNames(conversion.model, voices as ModelInfo[])}
          />
        </Link>
      )}
      <Link href={modelListingUrl()} className={keepGoingItem}>
        <KeepGoingRow
          icon={<Search size={18} />}
          iconBg="tw-bg-jam-line"
          title="Try another voice"
          sub="Browse thousands of community voices"
        />
      </Link>
      {plan === "basic" ? (
        <Credenza>
          <CredenzaTrigger asChild>
            <button className={keepGoingItem}>
              <KeepGoingRow
                icon={<Mic size={18} />}
                iconBg="tw-bg-[#FFE3CC]"
                title="Train your own voice"
                sub="Comes with the Creator plan"
              />
            </button>
          </CredenzaTrigger>
          <CredenzaContent>
            <CredenzaBody>
              <Upgrade
                type="Jammable Creator"
                fallback={() => actionHandler?.("manageSubscription")}
                callback={() => actionHandler?.("redirectToSettings")}
              />
            </CredenzaBody>
          </CredenzaContent>
        </Credenza>
      ) : plan === "free" ? (
        <button
          className={keepGoingItem}
          onClick={() => {
            openModal(
              "upgrade-modal",
              <UpgradeModal from="conversion-page" dailyLimit={false} />,
              { className: "tw-p-0" },
            );
          }}
        >
          <KeepGoingRow
            icon={<Sparkles size={18} />}
            iconBg="tw-bg-jam-lime"
            title="Pick a plan"
            sub="Make more covers and train your own voice"
          />
        </button>
      ) : (
        <Link href={customModelUrl()} className={keepGoingItem}>
          <KeepGoingRow
            icon={<Mic size={18} />}
            iconBg="tw-bg-[#FFE3CC]"
            title="Train your own voice"
            sub="Sing it in a voice only you have"
          />
        </Link>
      )}
    </div>
  );
};

// The source track (from inputFiles metadata), with its Spotify link when
// there is one.
export const SongCard = ({ conversion }: BlockProps) => {
  const files = (conversion?.inputFiles || []).filter(
    (file: any) => file?.metadata?.title,
  );
  if (files.length === 0) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[30px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-5">
      <Eyebrow>The song</Eyebrow>
      {files.map((file: any) => {
        const spotifyLink = file.metadata.streamingLinks?.find(
          (link: any) => link.platform === "spotify",
        )?.url;
        return (
          <button
            key={file._id}
            onClick={() => {
              if (spotifyLink) window.open(spotifyLink, "_blank");
            }}
            disabled={!spotifyLink}
            className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-font-[inherit] enabled:tw-cursor-pointer"
          >
            <span className="tw-relative tw-h-[52px] tw-w-[52px] tw-shrink-0 tw-overflow-hidden tw-rounded-2xl tw-bg-[#E3F7EC]">
              {file?.metadata?.image?.url && (
                <Image
                  src={file.metadata.image.url}
                  alt={file.metadata.title}
                  fill
                  sizes="52px"
                  className="tw-object-cover"
                />
              )}
            </span>
            <span className="tw-min-w-0 tw-flex-grow">
              <span className="tw-block tw-truncate tw-text-sm tw-font-extrabold tw-text-jam-ink">
                {file.metadata.title}
              </span>
              {file.metadata.artist && (
                <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-jam-muted">
                  {file.metadata.artist}
                </span>
              )}
            </span>
            {spotifyLink && (
              <FaSpotify className="tw-shrink-0 tw-text-[#1DB954]" size={22} />
            )}
          </button>
        );
      })}
    </div>
  );
};

// Shared-link visitors: one dark box that sends them to make their own.
export const MakeYourOwn = ({ conversion }: BlockProps) => {
  const voices = voicesOf(conversion);
  if (voices.length === 0) return null;
  return (
    <section className="tw-flex tw-flex-col tw-gap-5 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-flex-row md:tw-items-center md:tw-gap-6 md:tw-rounded-[40px] md:tw-px-9 md:tw-py-8">
      <div className="tw-flex">
        {voices.slice(0, 3).map((m, i) => (
          <span
            key={m._id || i}
            style={{ marginLeft: i ? -16 : 0, zIndex: 10 - i }}
            className="tw-relative tw-h-16 tw-w-16 tw-overflow-hidden tw-rounded-full tw-border-4 tw-border-solid tw-border-jam-ink tw-bg-jam-deep"
          >
            <VoiceImage
              model={m}
              fill
              sizes="64px"
              className="tw-object-cover"
            />
          </span>
        ))}
      </div>
      <div className="tw-flex-grow">
        <div className="jam-display tw-text-[24px] tw-font-extrabold tw-tracking-[-0.02em] md:tw-text-[30px]">
          Make your own with{" "}
          {voices.length === 1 ? "this voice" : "these voices"}
        </div>
        <div className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-jam-soft">
          Upload your audio, ready in about a minute.
        </div>
      </div>
      <Link
        href={reuseVoicesHref(voices)}
        className="tw-self-start tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lime tw-px-7 tw-py-[18px] tw-text-base tw-font-extrabold tw-text-jam-ink tw-no-underline md:tw-self-auto"
      >
        Make my cover
      </Link>
    </section>
  );
};

// Shared-link visitors: each voice on the cover with a way to use it.
export const VoiceList = ({ conversion }: BlockProps) => {
  const voices = voicesOf(conversion);
  if (voices.length === 0) return null;
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2.5">
      <CardHeading>
        <span className="tw-text-jam-ink">
          {voices.length === 1 ? "The voice" : "The voices"}
        </span>
      </CardHeading>
      {voices.map((m, i) => (
        <Link
          key={m._id || i}
          href={`/${m.urlParam}`}
          className="tw-flex tw-items-center tw-gap-3 tw-rounded-3xl tw-bg-jam-lav tw-p-3 tw-text-jam-ink tw-no-underline"
        >
          <span className="tw-relative tw-h-[52px] tw-w-[52px] tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-white">
            <VoiceImage
              model={m}
              fill
              sizes="52px"
              className="tw-object-cover"
            />
          </span>
          <span className="tw-min-w-0 tw-flex-grow tw-truncate tw-text-[15px] tw-font-extrabold">
            {m.artistname}
          </span>
          <span className="tw-shrink-0 tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold tw-text-jam-purple">
            Use voice
          </span>
        </Link>
      ))}
    </div>
  );
};
